import { Button } from "~/react/ui/button"
import { getCsrfToken } from "~/lib/utils"
import { capitalize } from "~/lib/text-helpers"

const t = (key: string, options?: Record<string, unknown>) => window.i18n.t(key, options)

const MAX_OFFSET = 10000

interface DiscoverResultsPaginationProps {
  pageSize: number
  offset: number
  hasMoreDomains: boolean
  hasDomains: boolean
  onPageChange: (offset: number) => void
  onPageSizeChange: (pageSize: number) => void
}

export function DiscoverResultsPagination({
  pageSize,
  offset,
  hasMoreDomains,
  hasDomains,
  onPageChange,
  onPageSizeChange,
}: DiscoverResultsPaginationProps) {
  const canGoPrevious = offset > 0
  const canGoNext = hasMoreDomains && offset + pageSize < MAX_OFFSET

  const handlePageSizeChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newPageSize = parseInt(e.target.value, 10)

    await fetch("/users/settings", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": getCsrfToken(),
      },
      body: JSON.stringify({
        user: { discover_results_page_size: newPageSize },
      }),
    })

    onPageSizeChange(newPageSize)
  }

  const handlePrevious = () => {
    const newOffset = Math.max(0, offset - pageSize)
    onPageChange(newOffset)
  }

  const handleNext = () => {
    const newOffset = offset + pageSize
    onPageChange(newOffset)
  }

  return (
    <div className="h-table__footer">
      <div className="h-table-pagination">
        <label className="sr-only" htmlFor="companies-results-table-per-page">
          {t("js.discover.index.number_of_companies_per_page")}
        </label>
        <select
          className="h-select h-select--sm"
          id="companies-results-table-per-page"
          aria-label={t("js.discover.index.number_of_companies_per_page")}
          value={pageSize}
          onChange={handlePageSizeChange}
        >
          {[20, 50, 100].map((size) => (
            <option key={size} value={size}>
              {t("js.discover.index.number_per_page", { number: size })}
            </option>
          ))}
        </select>

        <div className="h-table-pagination__end">
          {hasDomains && canGoPrevious ? (
            <Button size="sm" onClick={handlePrevious}>
              {capitalize(t("js.previous"))}
            </Button>
          ) : (
            <span className="h-button h-button--sm disabled">{capitalize(t("js.previous"))}</span>
          )}
          {canGoNext ? (
            <Button size="sm" onClick={handleNext} className="ml-3">
              {capitalize(t("js.next"))}
            </Button>
          ) : (
            <span className="h-button h-button--sm disabled ml-3">{capitalize(t("js.next"))}</span>
          )}
        </div>
      </div>
    </div>
  )
}
